import React, { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../__root';
import { useClientsStore } from '../../store/clientsStore';
import { Layout } from '@/client/layouts/layout';
import { parseString, parseBoolean, buildSearchParams } from '@/client/lib/searchParams';
import { usePersistedSearch } from '@/client/hooks/usePersistedSearch';
import { ResetFiltersButton } from '@/client/components/reset-filters-button';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import { columns } from '@/client/components/tables/clients-columns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Input } from '@/client/components/ui/input';
import { Switch } from '@/client/components/ui/switch';
import { Label } from '@/client/components/ui/label';
import { CacheTimestamp } from '@/client/components/cache-timestamp';

type ClientsSearch = {
  search?: string;
  showInactive?: boolean;
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'clients',
  validateSearch: (search: Record<string, unknown>): ClientsSearch => ({
    search: parseString(search.search),
    showInactive: parseBoolean(search.showInactive, false), // default: false
  }),
  beforeLoad: async ({ context }) => {
    // Redirect to home if not authenticated
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: ClientsComponent,
});

function ClientsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { clients, loading, error, cacheInfo, fetchClients } = useClientsStore();
  const navigate = useNavigate();
  const { search: searchFilter = '', showInactive = false } = useSearch({ from: '/clients' });

  // Persist search params across navigation
  usePersistedSearch('/clients', { search: undefined, showInactive: false });

  const [sorting, setSorting] = useState<SortingState>([]);

  // Update URL when search filter changes
  const setSearchFilter = (value: string) => {
    navigate({
      to: '/clients',
      search: buildSearchParams({ search: value || undefined, showInactive: showInactive || undefined }),
    });
  };

  // Update URL when show-inactive toggle changes
  const setShowInactive = (value: boolean) => {
    navigate({
      to: '/clients',
      search: buildSearchParams({ search: searchFilter || undefined, showInactive: value || undefined }),
    });
  };

  useEffect(() => {
    const loadClients = async () => {
      try {
        const token = await getAccessTokenSilently();
        await fetchClients(token, logout);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };

    loadClients();
  }, [getAccessTokenSilently, fetchClients]);

  const filteredClients = useMemo(() => {
    let filtered = clients;

    // Filter by active status
    if (!showInactive) {
      filtered = filtered.filter((client) => client.active);
    }

    // Filter by search text
    if (searchFilter) {
      filtered = filtered.filter((client) =>
        client.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    return filtered;
  }, [clients, searchFilter, showInactive]);

  const table = useReactTable({
    data: filteredClients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-semibold">Clients</h3>
            <CacheTimestamp cachedAt={cacheInfo?.cachedAt ?? null} />
          </div>

          {loading && <p>Loading clients...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && clients.length === 0 && (
            <p>No clients found.</p>
          )}

          {!loading && !error && clients.length > 0 && (
            <>
              <div className="flex justify-end items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-inactive"
                    checked={showInactive}
                    onCheckedChange={setShowInactive}
                  />
                  <Label htmlFor="show-inactive" className="cursor-pointer">
                    Show inactive clients
                  </Label>
                </div>
                <Input
                  placeholder="Filter by name..."
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-64"
                />
                <ResetFiltersButton
                  routePath="/clients"
                  defaultValues={{ search: undefined, showInactive: false }}
                />
              </div>
              <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => navigate({ to: `/clients/${row.original.clientID}` })}
                      className="cursor-pointer hover:bg-accent"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
        </section>
      </div>
    </Layout>
  );
}
